<script setup lang="ts">
const milestones = [
  {
    year: '5–20 anni',
    title: 'Hockey su ghiaccio · Hockey Club Lugano',
    description:
      'Crescita sportiva nell’hockey: disciplina, lavoro di squadra, intensità e gestione della pressione. Fondamenta atletiche e mentali costruite in anni di allenamenti e competizioni.',
    tags: ['Team sport', 'Intensità', 'Disciplina', 'Mentalità']
  },
  {
    year: '20–30 anni',
    title: 'Hockey amatoriale · continuità e passione',
    description:
      'Ho continuato a giocare in ambito amatoriale, mantenendo alta la frequenza sportiva e l’abitudine alla performance, senza perdere il gusto del gioco.',
    tags: ['Costanza', 'Motivazione', 'Sport life']
  },
  {
    year: 'Sempre (parallelamente)',
    title: 'MTB & Downhill · tecnica e controllo',
    description:
      'Mountain bike e downhill come scuola di tecnica: gestione del rischio, lettura del terreno, controllo e capacità di spingere quando conta.',
    tags: ['Tecnica', 'Skill', 'Adrenalina', 'Controllo']
  },
  {
    year: '2020 →',
    title: 'Ciclismo su strada · metodo e dati',
    description:
      'Con il Covid ho iniziato a fare ciclismo su strada in modo più strutturato. Ho sempre analizzato i miei allenamenti: carico, intensità, trend e progressi, con un approccio data-driven.',
    tags: ['Road cycling', 'Analisi', 'Zone', 'Progressione']
  },
  {
    year: '2021 →',
    title: 'Tennis · coordinazione e strategia',
    description:
      'Il tennis è diventato una costante: coordinazione, rapidità decisionale e lavoro tecnico. Un ottimo complemento per varietà e mobilità.',
    tags: ['Coordination', 'Tecnica', 'Mobilità']
  },
  {
    year: '2025',
    title: 'Endurance “big year” · obiettivi e risultati',
    description:
      'Anno di gare e sfide: Mezzo Ironman, Granfondo del Gottardo e traversata del lago. Un mix di resistenza, gestione dello sforzo e preparazione strutturata.',
    tags: ['Half Ironman', 'Granfondo', 'Open water', 'Endurance']
  }
]
const education = [
  {
    title: 'Bachelor of Science in Computer Science',
    place: 'Scuola Universitaria Professionale Svizzera Italiana (SUPSI)',
    year: '2017',
    details: ''
  },
  {
    title: 'Triathlon Coach Certification',
    place: 'United Endurance Sports Coaching Academy (UESCA)',
    year: '2026 - in corso',
    details:
      'Periodizzazione, test e valutazione, prescrizione dell’intensità (HR/Power/Pace), adattamenti e prevenzione infortuni.'
  }
]
const certifications = [
  {
    title: 'Coach Introduction',
    issuer: 'Training Peaks University',
    year: '2025',
    note: 'Basi di Training Peaks Coaching.'
  },
  {
    title: 'Build, Sell & Market Training Plans',
    issuer: 'Training Peaks University',
    year: '2025',
    note: 'Come costruire e vendere Training Plans.'
  },
  {
    title: 'TrainingPeaks Essentials',
    issuer: 'Training Peaks University',
    year: '2026',
    note: 'Tutto su Training Peaks Coaching.'
  },
  {
    title: 'Sports Psychology for Endurance Coaches',
    issuer: 'Training Peaks University',
    year: '2026',
    note: 'Fondamenta di Psicologia Sportiva.'
  },
  {
    title: 'TrainingPeaks Level 1 Coach Accreditation',
    issuer: 'Training Peaks University',
    year: '2026',
    note: ''
  }
]
</script>

<template>
  <section id="chi-sono" class="border-t border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950">
    <UContainer class="py-14 md:py-20">
      <!-- grid a 12 colonne per controllare meglio le larghezze -->
      <div class="grid gap-10 lg:grid-cols-12 lg:items-start">
        <!-- LEFT: 5/12 -->
        <div class="lg:col-span-5 max-w-none">
          <UBadge
            variant="subtle"
            class="border border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200"
          >
            Chi sono · Background & Credenziali
          </UBadge>

          <h2 class="mt-5 text-2xl font-semibold text-slate-900 dark:text-white md:text-3xl">
            Un approccio pratico, misurabile, umano.
          </h2>

          <p class="mt-3 text-slate-600 dark:text-slate-300">
            Mi chiamo <strong>Luca Margonar</strong> e lavoro con atleti amatori che vogliono migliorare
            senza trasformare la vita in una tabella. Credo in un coaching fatto di
            <strong>metodo</strong>, <strong>feedback reali</strong> e <strong>decisioni sostenibili</strong>.
          </p>

          <div
            class="mt-6 rounded-3xl border border-slate-200 bg-slate-50 p-6 dark:border-slate-800 dark:bg-slate-900/50"
          >
            <p class="text-sm font-semibold text-slate-900 dark:text-white">
              La mia filosofia
            </p>
            <p class="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
              Un piano efficace è quello che riesci a seguire. Costruiamo base, inseriamo qualità al momento giusto,
              e proteggiamo il recupero. I numeri aiutano a scegliere, ma il corpo resta il riferimento finale.
            </p>
          </div>

          <div class="mt-7 flex flex-wrap items-center gap-3">
            <UButton
              to="#contacts"
              color="primary"
              size="lg"
              class="shadow-sm"
            >
              Contattami
            </UButton>
          </div>

          <!-- Quick facts sotto, così la colonna sinistra “riempie” -->
          <UCard class="mt-8 rounded-3xl border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950">
            <template #header>
              <p class="text-sm font-semibold text-slate-900 dark:text-white">
                In breve
              </p>
              <p class="text-xs text-slate-500 dark:text-slate-400">
                come lavoro con te
              </p>
            </template>

            <div class="grid gap-3 sm:grid-cols-2">
              <div class="rounded-2xl border border-slate-200 p-4 dark:border-slate-800">
                <p class="text-sm font-semibold text-slate-900 dark:text-white">
                  Metodo
                </p>
                <p class="mt-1 text-sm text-slate-600 dark:text-slate-300">
                  Test → zone → progressione. Revisioni regolari e scelte basate su dati + sensazioni.
                </p>
              </div>

              <div class="rounded-2xl border border-slate-200 p-4 dark:border-slate-800">
                <p class="text-sm font-semibold text-slate-900 dark:text-white">
                  Sostenibilità
                </p>
                <p class="mt-1 text-sm text-slate-600 dark:text-slate-300">
                  Carico realistico, recupero rispettato e adattamenti rapidi quando la vita cambia.
                </p>
              </div>

              <div class="rounded-2xl border border-slate-200 p-4 dark:border-slate-800">
                <p class="text-sm font-semibold text-slate-900 dark:text-white">
                  Comunicazione
                </p>
                <p class="mt-1 text-sm text-slate-600 dark:text-slate-300">
                  Check-in chiari, obiettivi misurabili e feedback concreti.
                </p>
              </div>

              <div class="rounded-2xl border border-slate-200 p-4 dark:border-slate-800">
                <p class="text-sm font-semibold text-slate-900 dark:text-white">
                  Target
                </p>
                <p class="mt-1 text-sm text-slate-600 dark:text-slate-300">
                  Amatori motivati: triathlon, ciclismo, corsa. Dal “finisher” al “PB”.
                </p>
              </div>
            </div>
          </UCard>
        </div>

        <!-- RIGHT: 7/12 -->
        <div class="lg:col-span-7 grid gap-4">
          <!-- Esperienze (timeline) -->
          <UCard class="rounded-3xl border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950">
            <template #header>
              <p class="text-sm font-semibold text-slate-900 dark:text-white">
                Esperienza
              </p>
              <p class="text-xs text-slate-500 dark:text-slate-400">
                il percorso che mi ha portato qui
              </p>
            </template>

            <ol class="space-y-5">
              <li v-for="m in milestones" :key="m.year" class="flex gap-4">
                <div class="pt-1">
                  <div class="h-2.5 w-2.5 rounded-full bg-slate-300 dark:bg-slate-700" />
                </div>

                <div class="min-w-0">
                  <div class="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <p class="text-xs font-semibold text-slate-500 dark:text-slate-400">
                      {{ m.year }}
                    </p>
                    <p class="text-sm font-semibold text-slate-900 dark:text-white">
                      {{ m.title }}
                    </p>
                  </div>

                  <p class="mt-1 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
                    {{ m.description }}
                  </p>

                  <div class="mt-2 flex flex-wrap gap-2">
                    <UBadge
                      v-for="t in m.tags"
                      :key="t"
                      variant="subtle"
                      class="border border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200"
                    >
                      {{ t }}
                    </UBadge>
                  </div>
                </div>
              </li>
            </ol>
          </UCard>

          <!-- Studi + Certificazioni: stack verticale su desktop (niente schiacciamento) -->
        </div>
        <div class="lg:col-span-12 grid gap-4 sm:grid-cols-2">
          <!-- STUDI -->
          <UCard class="rounded-3xl border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950">
            <template #header>
              <p class="text-sm font-semibold text-slate-900 dark:text-white">
                Studi
              </p>
              <p class="text-xs text-slate-500 dark:text-slate-400">
                formazione
              </p>
            </template>

            <div class="space-y-3">
              <div
                v-for="e in education"
                :key="e.title"
                class="rounded-2xl border border-slate-200 p-4 dark:border-slate-800"
              >
                <div class="flex items-start justify-between gap-3">
                  <div class="min-w-0">
                    <p class="text-sm font-semibold text-slate-900 dark:text-white">
                      {{ e.title }}
                    </p>
                    <p class="text-xs text-slate-500 dark:text-slate-400">
                      {{ e.place }}
                    </p>
                  </div>
                  <UBadge
                    variant="subtle"
                    class="border border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200"
                  >
                    {{ e.year }}
                  </UBadge>
                </div>

                <p class="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
                  {{ e.details }}
                </p>
              </div>
            </div>
          </UCard>

          <!-- CERTIFICAZIONI -->
          <UCard class="rounded-3xl border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950">
            <template #header>
              <div class="flex items-center justify-between gap-4">
                <!-- Testo header -->
                <div>
                  <p class="text-sm font-semibold text-slate-900 dark:text-white">
                    Certificazioni
                  </p>
                  <p class="text-xs text-slate-500 dark:text-slate-400">
                    aggiornamento continuo
                  </p>
                </div>

                <!-- Logo TrainingPeaks — light/dark switch -->
                <img
                  src="/images/coach_badge_positive.png"
                  alt="TrainingPeaks Level 1 Coach Accreditation"
                  class="h-14 w-auto object-contain dark:hidden"
                >
                <img
                  src="/images/coach_badge_negative.png"
                  alt="TrainingPeaks Level 1 Coach Accreditation"
                  class="hidden h-14 w-auto object-contain dark:block"
                >
              </div>
            </template>

            <div class="space-y-3">
              <div
                v-for="c in certifications"
                :key="c.title"
                class="rounded-2xl border border-slate-200 p-4 dark:border-slate-800"
              >
                <div class="flex items-start justify-between gap-3">
                  <div class="min-w-0">
                    <p class="text-sm font-semibold text-slate-900 dark:text-white">
                      {{ c.title }}
                    </p>
                    <p class="text-xs text-slate-500 dark:text-slate-400">
                      {{ c.issuer }}
                    </p>
                  </div>
                  <UBadge
                    variant="subtle"
                    class="border border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200"
                  >
                    {{ c.year }}
                  </UBadge>
                </div>

                <p class="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
                  {{ c.note }}
                </p>
              </div>
            </div>
          </UCard>
        </div>
      </div>
    </UContainer>
  </section>
</template>

<style scoped>
/* segue lo stile della landing */
</style>
